import { useState } from "react";

import { Button, Icon, Notification } from "@stellar/design-system";

import { AssetAmount } from "@/components/AssetAmount";
import { DialogModal } from "@/components/DialogModal";
import { Table } from "@/components/Table";

import { MULTISIG_SIGNER_ROLE } from "@/constants/multisigEnrollment";
import { UNENROLLMENT_REQUIRED_SIGNATURES } from "@/constants/multisigUnenrollment";

import type { ApiDisbursement, ApiUser } from "@/types";

import type { UnenrollmentPerspective } from "./UnenrollmentSignModal";

import "../styles.scss";

export type UnenrollmentDisbursement = Pick<
  ApiDisbursement,
  "id" | "name" | "created_by" | "total_payments" | "total_amount" | "asset"
>;

type UnenrollmentReviewProps = {
  currentUserId: string;
  currentUserName: string;
  /** Users whose `multisig_signer.status` is ACTIVE (a snapshot once the removal has completed). */
  coSigners: ApiUser[];
  disbursements: UnenrollmentDisbursement[];
  collectedSignatures: number;
  requiredSignatures?: number;
  /** Whether the current Owner's signature is among the collected ones. */
  hasMySignature: boolean;
  canSign: boolean;
  /**
   * The Owner who started the request sees "Cancel and keep co-signing on";
   * the Owner responding to it sees "Reject request".
   */
  perspective?: UnenrollmentPerspective;
  /** Who started the request; required copy for the responder. */
  initiatorName?: string;
  /** The other Owner who signed, once known. */
  secondSignerName?: string;
  /** "Sunday, Oct 4, 2026 at 2:15 PM EDT": when the request expires. */
  deadline?: string | null;
  /** Both Owners signed and the network confirmed: the page becomes the receipt. */
  isFinished?: boolean;
  /** The cancel or reject request is in flight; its button is locked meanwhile. */
  isCancelling?: boolean;
  /** Why the last cancel or reject request was refused, shown above the review. */
  cancelError?: string | null;
  onSign: () => void;
  /** Cancel (initiator) or reject (responder) the request. */
  onCancel: () => void;
  onExit: () => void;
};

const fullName = (person?: { first_name: string; last_name: string }) =>
  person ? `${person.first_name} ${person.last_name}`.trim() : "";

const firstName = (name: string) => name.trim().split(/\s+/)[0] || name;

const signerRoleText = (user: ApiUser) =>
  user.multisig_signer?.role === MULTISIG_SIGNER_ROLE.OWNER ? "Owner" : "Financial controller";

type SignatureRow = {
  name: string;
  isMe: boolean;
  signed: boolean;
  showSign: boolean;
  caption: string;
};

const pendingDisbursementsCopy = (count: number, isFinished: boolean) => {
  if (isFinished) {
    return count === 1
      ? "1 disbursement was canceled and returned to drafts, and its creator was notified."
      : `${count} disbursements were canceled and returned to drafts, and their creators were notified.`;
  }
  return count === 1
    ? "1 disbursement is waiting for signatures. It'll be canceled and returned to drafts, and its creator will be notified."
    : `${count} disbursements are waiting for signatures. They'll be canceled and returned to drafts, and their creators will be notified.`;
};

export const UnenrollmentReview = ({
  currentUserId,
  currentUserName,
  coSigners,
  disbursements,
  collectedSignatures,
  requiredSignatures = UNENROLLMENT_REQUIRED_SIGNATURES,
  hasMySignature,
  canSign,
  perspective = "initiator",
  initiatorName,
  secondSignerName,
  deadline,
  isFinished = false,
  isCancelling = false,
  cancelError,
  onSign,
  onCancel,
  onExit,
}: UnenrollmentReviewProps) => {
  const [showDisbursements, setShowDisbursements] = useState(false);
  const [isConfirmVisible, setIsConfirmVisible] = useState(false);

  const isResponder = perspective === "responder";
  const otherSigned = collectedSignatures - (hasMySignature ? 1 : 0) >= 1;
  const allSigned = collectedSignatures >= requiredSignatures;
  const pendingCount = disbursements.length;
  const initiator = initiatorName?.trim() || "Another Owner";

  const me: SignatureRow = {
    name: currentUserName,
    isMe: true,
    signed: hasMySignature,
    showSign: !hasMySignature && canSign && !isFinished,
    caption: "Owner",
  };
  const other: SignatureRow = isResponder
    ? { name: initiator, isMe: false, signed: otherSigned, showSign: false, caption: "Owner" }
    : {
        name: otherSigned && secondSignerName ? secondSignerName : "Any other Owner",
        isMe: false,
        signed: otherSigned,
        showSign: false,
        // Once the initiator has signed, the other Owners have been asked to respond.
        caption: !otherSigned && hasMySignature && !isFinished ? "Owners notified" : "Owner",
      };
  const rows: SignatureRow[] = isResponder ? [other, me] : [me, other];

  const renderMark = (isOn: boolean) => (
    <span className={`MultisigManage__sigMark${isOn ? " is-on" : ""}`}>
      {isOn ? <Icon.Check /> : null}
    </span>
  );

  const renderPauseCard = () => {
    if (isFinished) {
      return (
        <div className="MultisigManage__card">
          <div className="MultisigManage__done">
            <span className="MultisigManage__doneCheck">
              <Icon.Check />
            </span>
            <div>
              <h2 className="MultisigManage__title">Co-signing is off</h2>
              <p className="MultisigManage__p">
                From now on, disbursements go out without a second approval.
              </p>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="MultisigManage__card">
        <div className="MultisigManage__sigHead">
          <h2 className="MultisigManage__title">Co-signing is paused</h2>
          <span className="MultisigManage__badge MultisigManage__badge--paused">
            <svg
              width="10"
              height="10"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M9 5v14M15 5v14" />
            </svg>
            Paused
          </span>
        </div>
        <p className="MultisigManage__p">
          {isResponder
            ? `${initiator} started turning off co-signing. Until you sign or reject, ${firstName(initiator)} cancels, or the request expires:`
            : "Until the second Owner signs, you cancel, or the request expires:"}
        </p>
        <ul className="MultisigManage__list">
          <li>New disbursements can&apos;t be created.</li>
          <li>Disbursements waiting for signatures can&apos;t collect any more signatures.</li>
          <li>Co-signers can&apos;t be added or removed.</li>
        </ul>
        {deadline ? (
          <p className="MultisigManage__p MultisigManage__note">
            If {isResponder ? "you don't" : "two Owners don't"} sign by <strong>{deadline}</strong>,
            the request expires, the pause ends, and co-signing stays on.
          </p>
        ) : null}
      </div>
    );
  };

  const renderSignaturesIntro = () => {
    if (isFinished) return null;
    return (
      <p className="MultisigManage__p">
        Two Owners need to sign in VIA Wallet
        {deadline ? (
          <>
            {" "}
            by <strong>{deadline}</strong>
          </>
        ) : null}{" "}
        to turn off co-signing.
        {isResponder ? " Your signature is the last one needed." : null}
      </p>
    );
  };

  const confirm = isResponder
    ? {
        button: "Reject request",
        title: "Reject the request to turn off co-signing?",
        body: `Co-signing stays on and the co-signing pause ends. ${initiator} will see that you rejected the request.`,
        action: "Reject request",
      }
    : {
        button: "Cancel and keep co-signing on",
        title: "Cancel your request to turn off co-signing?",
        body: "Co-signing stays on and the co-signing pause ends right away. Other Owners will see that you canceled it.",
        action: "Cancel request",
      };

  return (
    <div className="MultisigManage">
      <div className="MultisigManage__wiz">
        {cancelError ? (
          <div className="MultisigManage__error">
            <Notification
              variant="error"
              title={
                isResponder
                  ? "Couldn't reject the request to turn off co-signing"
                  : "Couldn't cancel turning off co-signing"
              }
              isFilled
            >
              {cancelError}
            </Notification>
          </div>
        ) : null}

        {renderPauseCard()}

        <div className="MultisigManage__card">
          <h2 className="MultisigManage__title">
            {isFinished ? "What changed" : "Review what will change"}
          </h2>
          {isFinished ? null : (
            <p className="MultisigManage__p">
              {isResponder
                ? "These changes happen once you sign."
                : "These changes happen once a second Owner signs."}
            </p>
          )}

          <h3 className="MultisigManage__sub">
            {isFinished ? "Co-signers removed" : "Co-signers to be removed"}{" "}
            <span className="MultisigManage__count">({coSigners.length})</span>
          </h3>
          <div className="MultisigManage__block">
            <Table>
              <Table.Header>
                <Table.HeaderCell>Name</Table.HeaderCell>
                <Table.HeaderCell>Email</Table.HeaderCell>
                <Table.HeaderCell>Role</Table.HeaderCell>
              </Table.Header>
              <Table.Body>
                {coSigners.map((u) => (
                  <Table.BodyRow key={u.id}>
                    <Table.BodyCell>
                      {fullName(u)}
                      {u.id === currentUserId ? (
                        <span className="MultisigManage__muted"> (you)</span>
                      ) : null}
                    </Table.BodyCell>
                    <Table.BodyCell>
                      <span className="MultisigManage__muted">{u.email}</span>
                    </Table.BodyCell>
                    <Table.BodyCell>
                      <span className="MultisigManage__muted">{signerRoleText(u)}</span>
                    </Table.BodyCell>
                  </Table.BodyRow>
                ))}
              </Table.Body>
            </Table>
          </div>

          {pendingCount > 0 ? (
            <>
              <h3 className="MultisigManage__sub">
                {isFinished ? "Disbursements moved to drafts" : "Disbursements to move to drafts"}{" "}
                <span className="MultisigManage__count">({pendingCount})</span>
              </h3>
              <p className="MultisigManage__p">
                {pendingDisbursementsCopy(pendingCount, isFinished)}
              </p>
              <button
                type="button"
                className="MultisigManage__toggle"
                aria-expanded={showDisbursements}
                onClick={() => setShowDisbursements(!showDisbursements)}
              >
                {showDisbursements ? "Hide disbursements" : "Show disbursements"}
                <Icon.ChevronDown />
              </button>
              {showDisbursements ? (
                <div className="MultisigManage__block">
                  <Table>
                    <Table.Header>
                      <Table.HeaderCell>Disbursement</Table.HeaderCell>
                      <Table.HeaderCell>Created by</Table.HeaderCell>
                      <Table.HeaderCell textAlign="right">Payments</Table.HeaderCell>
                      <Table.HeaderCell textAlign="right">Total amount</Table.HeaderCell>
                    </Table.Header>
                    <Table.Body>
                      {disbursements.map((d) => (
                        <Table.BodyRow key={d.id}>
                          <Table.BodyCell>{d.name}</Table.BodyCell>
                          <Table.BodyCell>
                            <span className="MultisigManage__muted">{fullName(d.created_by)}</span>
                          </Table.BodyCell>
                          <Table.BodyCell textAlign="right">{d.total_payments}</Table.BodyCell>
                          <Table.BodyCell textAlign="right">
                            <AssetAmount amount={d.total_amount} assetCode={d.asset.code} />
                          </Table.BodyCell>
                        </Table.BodyRow>
                      ))}
                    </Table.Body>
                  </Table>
                </div>
              ) : null}
            </>
          ) : null}
        </div>

        <div className="MultisigManage__card">
          <div className="MultisigManage__sigHead">
            <div>
              <h2 className="MultisigManage__title">Signatures</h2>
              {renderSignaturesIntro()}
            </div>
            <span
              className={`MultisigManage__badge${allSigned ? " MultisigManage__badge--success" : ""}`}
            >
              {collectedSignatures} of {requiredSignatures} signatures
            </span>
          </div>
          <div className="MultisigManage__sigList">
            {rows.map((row, index) => (
              <div className="MultisigManage__sig" key={row.isMe ? "me" : "other"}>
                <span className="MultisigManage__sigNum">Signature {index + 1}</span>
                <div className="MultisigManage__sigMain">
                  {renderMark(row.signed)}
                  <span className="MultisigManage__sigWho">
                    <span className="MultisigManage__sigName">
                      {row.name}
                      {row.isMe ? <span className="MultisigManage__you"> (you)</span> : null}
                    </span>
                    <span className="MultisigManage__sigRole">{row.caption}</span>
                  </span>
                  <span className="MultisigManage__sigAction">
                    {row.signed ? (
                      <span className="MultisigManage__sigState MultisigManage__sigState--ok">
                        Signed
                      </span>
                    ) : row.showSign ? (
                      <Button variant="primary" size="md" onClick={onSign}>
                        Sign to turn off
                      </Button>
                    ) : (
                      <span className="MultisigManage__sigState">Not signed yet</span>
                    )}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {isFinished ? (
          <div className="MultisigManage__actions MultisigManage__actions--bare MultisigManage__actions--end">
            <Button variant="tertiary" size="md" onClick={onExit}>
              Back to Settings
            </Button>
          </div>
        ) : (
          <div className="MultisigManage__actions MultisigManage__actions--bare">
            <div className="MultisigManage__destructiveOutline">
              <Button
                variant="tertiary"
                size="md"
                isLoading={isCancelling}
                onClick={() => setIsConfirmVisible(true)}
              >
                {confirm.button}
              </Button>
            </div>
            <Button
              variant="tertiary"
              size="md"
              disabled={!isResponder && !hasMySignature}
              onClick={onExit}
            >
              Back to Settings
            </Button>
          </div>
        )}
      </div>

      <DialogModal
        visible={isConfirmVisible}
        onClose={() => setIsConfirmVisible(false)}
        title={confirm.title}
        width="50"
        footerAlignment="stretch"
        primaryAction={{
          label: "Go back",
          variant: "tertiary",
          onClick: () => setIsConfirmVisible(false),
        }}
        secondaryAction={{
          label: confirm.action,
          variant: "destructive",
          onClick: () => {
            setIsConfirmVisible(false);
            onCancel();
          },
        }}
      >
        <p>{confirm.body}</p>
      </DialogModal>
    </div>
  );
};
